import { type FollowupReasonListItem, type FollowupReasonListQuery } from '@utazzo/shared';
import { UzStatusBadge } from '@utazzo/ui';
import React from 'react';

import { FollowupReasonFormDrawer } from '../components/FollowupReasonFormDrawer';
import { useFollowupReasonsList } from '../use-followup-reasons';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

/** Server search only fires from this many characters. */
const MIN_SEARCH_LENGTH = 3;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
];

/**
 * Follow-up Reason master CRUD. Add + edit are exposed; delete is
 * deliberately NOT - the reason may be referenced from a future
 * `lead_follow_ups.followup_reason_id` FK, so dropping a row would
 * orphan history. Operators turn unused reasons `Active: off` via the
 * edit form to hide them from the follow-up form without breaking
 * past data.
 */
export const FollowupReasonsListPage = (): React.JSX.Element => {
  const columns: ListScreenColumns<FollowupReasonListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<FollowupReasonListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            <span className="text-xs text-zinc-500">{row.code}</span>
          </div>
        ),
      },
      {
        id: 'description',
        header: 'Description',
        cell: (row) => <span className="text-zinc-600">{row.description ?? '-'}</span>,
      },
      {
        id: 'sortOrder',
        header: 'Sort',
        sortable: true,
        width: '80px',
        cell: (row) => <span>{row.sortOrder}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<FollowupReasonListItem, FollowupReasonListQuery>
      title="Follow-up Reasons"
      description="Reasons that drive a lead follow-up (e.g. Customer requested callback, Quote needs revision). Used by the follow-up form to classify the reason behind each scheduled call."
      useList={useFollowupReasonsList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        return {
          ...base,
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      }}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add follow-up reason' }}
      renderDrawer={({ open, mode, row, close }) => (
        <FollowupReasonFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          followupReason={row}
        />
      )}
      // Deletion is intentionally not exposed - a reason may be
      // referenced by a future `lead_follow_ups.followup_reason_id`.
      // Operators deactivate via the edit form (Active: off) instead.
      rowActions={{
        edit: true,
      }}
      copy={{
        searchPlaceholder: 'Search by name or code (min 3 characters)',
        emptyTitle: 'No follow-up reasons match these filters',
        emptyDescription: 'Adjust the status filter or clear the search box.',
        errorTitle: 'Failed to load follow-up reasons',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
